import { useEffect, useRef } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

function getSiteUrl(): string {
  try {
    return import.meta.env.VITE_SITE_URL || "https://superbuy.study";
  } catch {
    return "https://superbuy.study";
  }
}

const SEO_META_IDS = {
  description: "seo-meta-description",
  keywords: "seo-meta-keywords",
  ogTitle: "seo-og-title",
  ogDescription: "seo-og-description",
  ogUrl: "seo-og-url",
  ogType: "seo-og-type",
  ogImage: "seo-og-image",
  twitterCard: "seo-twitter-card",
  twitterTitle: "seo-twitter-title",
  twitterDescription: "seo-twitter-description",
  canonical: "seo-canonical",
  robots: "seo-robots",
  jsonLd: "seo-jsonld",
};

function setOrCreateMeta(id: string, name: string, content: string, isProperty: boolean = false) {
  let el = document.getElementById(id) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.id = id;
    if (isProperty) {
      el.setAttribute("property", name);
    } else {
      el.setAttribute("name", name);
    }
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(id: string) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

function setOrCreateLink(id: string, rel: string, href: string) {
  let el = document.getElementById(id) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.id = id;
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id: string, data: Record<string, unknown> | Record<string, unknown>[]) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data, null, 2);
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = "website",
  ogImage,
  jsonLd,
  noIndex = false,
}: SEOHeadProps) {
  const siteUrl = getSiteUrl();
  const fullCanonical = `${siteUrl.replace(/\/$/, "")}${canonicalPath}`;
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    document.title = title;

    setOrCreateMeta(SEO_META_IDS.description, "description", description);
    setOrCreateMeta(SEO_META_IDS.ogTitle, "og:title", title, true);
    setOrCreateMeta(SEO_META_IDS.ogDescription, "og:description", description, true);
    setOrCreateMeta(SEO_META_IDS.ogUrl, "og:url", fullCanonical, true);
    setOrCreateMeta(SEO_META_IDS.ogType, "og:type", ogType, true);
    setOrCreateMeta(SEO_META_IDS.twitterCard, "twitter:card", "summary_large_image");
    setOrCreateMeta(SEO_META_IDS.twitterTitle, "twitter:title", title);
    setOrCreateMeta(SEO_META_IDS.twitterDescription, "twitter:description", description);
    setOrCreateMeta(SEO_META_IDS.robots, "robots", noIndex ? "noindex, nofollow" : "index, follow");
    setOrCreateLink(SEO_META_IDS.canonical, "canonical", fullCanonical);

    if (keywords) {
      setOrCreateMeta(SEO_META_IDS.keywords, "keywords", keywords);
    } else {
      removeMeta(SEO_META_IDS.keywords);
    }

    if (ogImage) {
      setOrCreateMeta(SEO_META_IDS.ogImage, "og:image", ogImage, true);
    } else {
      removeMeta(SEO_META_IDS.ogImage);
    }

    if (jsonLd) {
      setJsonLd(SEO_META_IDS.jsonLd, jsonLd);
    } else {
      removeMeta(SEO_META_IDS.jsonLd);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [title, description, keywords, fullCanonical, ogType, ogImage, jsonLd, noIndex]);

  return null;
}